import React from "react";
import { useTranslation } from "locales";
import "./style.scss";

interface Props {
  /** Number of currently selected messages. Toolbar renders only when > 0. */
  count: number;
  /** True when at least one of the selected messages is deletable by the
   *  current user (own message or moderator). When false, the Delete button
   *  is hidden — Forward/Cancel still available. */
  canDeleteAny: boolean;
  onDelete: () => void;
  onForward: () => void;
  onCancel: () => void;
}

/**
 * Header replacement that appears while the user is in multi-select mode in
 * a chat. Shows the selection count and the bulk actions (delete, forward).
 * Hosted by the parent chat page so its lifetime matches the page's
 * `selectionMode` state.
 */
function ChatSelectionToolbar({ count, canDeleteAny, onDelete, onForward, onCancel }: Props) {
  const { t } = useTranslation();
  if (count === 0) return null;

  return (
    <div className="chat-selection-toolbar" role="toolbar" aria-label={t("Selected messages")}>
      <button
        type="button"
        className="chat-selection-toolbar__icon-btn"
        onClick={onCancel}
        title={t("Cancel selection")}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
        </svg>
      </button>
      <div className="chat-selection-toolbar__count">
        {t("{{count}} selected", { count })}
      </div>
      <div className="chat-selection-toolbar__actions">
        <button
          type="button"
          className="chat-selection-toolbar__icon-btn"
          onClick={onForward}
          title={t("Forward")}
          aria-label={t("Forward")}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
            <path d="M14 9V5l7 7-7 7v-4.1c-5 0-8.5 1.6-11 5.1 1-5 4-10 11-11z" fill="currentColor" />
          </svg>
        </button>
        {canDeleteAny && (
          <button
            type="button"
            className="chat-selection-toolbar__icon-btn __danger"
            onClick={onDelete}
            title={t("Delete")}
            aria-label={t("Delete")}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export default ChatSelectionToolbar;
